import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getRecoveryData,
  clearRecoveryData,
  clearLocalStorageBackup,
} from "../services/sessionRecoveryService";
import { AlertTriangle, Play, X } from "lucide-react";

const RECOVERY_STATUSES = ["recording", "paused", "processing", "review"];
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

function statusLabel(status) {
  switch (status) {
    case "recording": return "Interrupted recording";
    case "paused":    return "Paused session";
    case "processing": return "Note still generating";
    case "review":    return "Note awaiting review";
    default:          return "Unfinished session";
  }
}

function statusActionLabel(status) {
  switch (status) {
    case "recording": return "Resume Recording";
    case "paused":    return "Resume Recording";
    case "processing": return "Resume Generation";
    case "review":    return "Resume Review";
    default:          return "Resume";
  }
}

export default function RecoveryBanner() {
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const reload = useCallback(async () => {
    const rows = await getRecoveryData();
    const interrupted = rows
      .filter((r) => RECOVERY_STATUSES.includes(r.status))
      .filter((r) => Date.now() - (r.lastSaved || 0) < MAX_AGE_MS)
      .sort((a, b) => (b.lastSaved || 0) - (a.lastSaved || 0))[0];
    setRow(interrupted || null);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!row || dismissed) return null;

  const handleResume = () => {
    // Route to the right surface based on where the session was when interrupted.
    if (row.status === "processing") {
      navigate("/session/processing", {
        state: {
          sessionId: row.id,
          sessionData: {
            duration: row.duration,
            flaggedSegments: row.flaggedNotes || [],
          },
          transcript: row.transcript || "",
          individualName: row.individualName,
          individualId: row.individualId,
          dob: row.dob,
          locationType: row.locationType,
          location: row.location,
          serviceType: row.serviceType || "ICM Weekly Visit",
          knownMedications: row.knownMedications || [],
          knownDiagnoses: row.knownDiagnoses || [],
          duration: row.duration || 1800,
          extractedFacts: row.extractedFacts || [],
          gpsLocation: row.gpsLocation || null,
          draftNoteId: row.draftNoteId || null,
          appointmentId: row.appointmentId || null,
        },
      });
      return;
    }

    // recording / paused / review → ActiveSessionPage's existing recovery
    // modal will pick this up on mount.
    navigate("/session/new", {
      state: {
        individualName: row.individualName,
        individualId: row.individualId,
        dob: row.dob,
        locationType: row.locationType,
        location: row.location,
        serviceType: row.serviceType,
        knownMedications: row.knownMedications || [],
        knownDiagnoses: row.knownDiagnoses || [],
      },
    });
  };

  const handleDiscard = async () => {
    if (
      !window.confirm(
        `Discard the unfinished session for ${row.individualName || "this individual"}? The audio is still saved on this device under Settings → Recover Lost Sessions, but the transcript backup will be erased.`
      )
    ) {
      return;
    }
    await clearRecoveryData(row.id);
    clearLocalStorageBackup(row.id);
    setDismissed(true);
  };

  const lastSavedStr = row.lastSaved
    ? new Date(row.lastSaved).toLocaleString()
    : "Unknown";

  const wordCount =
    typeof row.transcript === "string"
      ? row.transcript.split(/\s+/).filter(Boolean).length
      : 0;

  return (
    <div
      className="mx-4 mt-3 p-3 rounded-xl flex items-center gap-3"
      style={{
        background: "rgba(236,201,75,0.06)",
        border: "1px solid rgba(236,201,75,0.2)",
      }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: "rgba(236,201,75,0.15)" }}
      >
        <AlertTriangle size={16} className="text-yellow-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-yellow-400 truncate">
          {statusLabel(row.status)}
          {row.individualName ? ` — ${row.individualName}` : ""}
        </div>
        <div className="text-[11px] text-gray-500 truncate">
          Last saved {lastSavedStr}
          {wordCount > 0 && ` · ${wordCount} words captured`}
          {row.flaggedNotes?.length > 0 && ` · ${row.flaggedNotes.length} flagged`}
        </div>
      </div>
      <button
        className="px-3 py-2 rounded-lg text-[11px] font-semibold text-white flex items-center gap-1.5 shrink-0"
        style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)" }}
        onClick={handleResume}
      >
        <Play size={12} /> {statusActionLabel(row.status)}
      </button>
      <button
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: "rgba(252,129,129,0.06)", border: "1px solid rgba(252,129,129,0.15)" }}
        onClick={handleDiscard}
        title="Discard"
      >
        <X size={14} className="text-red-400" />
      </button>
    </div>
  );
}
